/**
 * Shared client-side projection of the tenant lifecycle state machine in
 * `services/api/src/modules/admin/tenant-lifecycle.service.ts`.
 *
 * Keeping this map in one module prevents the tenant list, Tenant 360 and GTCC
 * from offering different verbs for the same server state.
 */
export const TENANT_SUBSCRIPTION_STATUSES = [
  "trial",
  "active",
  "paused",
  "suspended",
  "cancelled",
  "expired",
] as const;

export type TenantSubscriptionStatus = (typeof TENANT_SUBSCRIPTION_STATUSES)[number];
export type TenantLifecycleVerb = "pause" | "resume" | "suspend" | "cancel";

const tenantLifecyclePathByVerb: Record<TenantLifecycleVerb, (tenantId: string) => string> = {
  pause: (tenantId) => `/v1/admin/tenants/${tenantId}/lifecycle/pause`,
  resume: (tenantId) => `/v1/admin/tenants/${tenantId}/lifecycle/resume`,
  suspend: (tenantId) => `/v1/admin/tenants/${tenantId}/lifecycle/suspend`,
  cancel: (tenantId) => `/v1/admin/tenants/${tenantId}/lifecycle/cancel`,
};

export function tenantLifecyclePath(tenantId: string, verb: TenantLifecycleVerb): string {
  return tenantLifecyclePathByVerb[verb](tenantId);
}

/**
 * Mirrors the soft-state policies in `tenant-lifecycle.service.ts`. Trial must
 * include resume because that is the manual end-of-trial path; cancelled and
 * expired remain terminal in every operator UI.
 */
export function allowedTenantLifecycleVerbs(
  status: TenantSubscriptionStatus | undefined,
): readonly TenantLifecycleVerb[] {
  switch (status) {
    case "trial":
      return ["resume", "pause", "suspend", "cancel"];
    case "active":
      return ["pause", "suspend", "cancel"];
    case "paused":
      return ["resume", "suspend", "cancel"];
    case "suspended":
      return ["resume", "cancel"];
    case "cancelled":
    case "expired":
    default:
      return [];
  }
}
